/* The access-policy workspace's reading of rules: what each one does, what they grant together, what
 * a publish changes, and how one request would be decided.
 *
 * **Every answer here is about the rules as the platform evaluates them**, which the authoring route
 * states in its own words: `allow` is true only when some allow rule matches and no deny rule does,
 * so a broad allow can be narrowed by a specific deny, and a request matching nothing is refused.
 * Each function below is that one sentence applied to a different question, kept together so the
 * editor, the summary and the request checker cannot drift into three readings of it.
 */

import type {
  BundleVersion,
  PolicyRule,
  PolicyVocabulary,
} from '@/shared/api/generated/policy';

import type { BumpKind } from '../api/policy';
import type { DraftRule } from './builder';
import { isComplete } from './builder';
import type { EnforcementReading } from './bundles';

/* ---------------------------------------------------------------- identity of a rule */

/** A rule's identity for comparison. A rule that changed effect is a different rule. */
export function keyOfRule(rule: PolicyRule): string {
  return [
    rule.effect,
    rule.role,
    rule.action,
    rule.resource_type,
    rule.resource_id ?? '*',
  ].join('|');
}

/** The same identity for a row being edited, with an unanswered field as empty. */
export function keyOfDraft(rule: DraftRule): string {
  return [
    rule.effect ?? '',
    rule.role ?? '',
    rule.action ?? '',
    rule.resourceType ?? '',
    rule.resourceId ?? '*',
  ].join('|');
}

/** Whether the rows on screen say something other than the rules they were opened from. */
export function differsFrom(
  rows: readonly DraftRule[],
  base: readonly PolicyRule[],
): boolean {
  if (rows.length !== base.length) return true;
  return rows.some((row, index) => {
    const original = base[index];
    return original === undefined || keyOfDraft(row) !== keyOfRule(original);
  });
}

/** The editor's own state, lifted to the page so switching tabs keeps unsaved work.
 *
 * `pendingOne` holds the rows whose scope is "one resource" with none chosen yet. It cannot be
 * modelled as a null id, because a null id *is* "every resource" — a row would silently widen from
 * one dataset to all of them the moment somebody looked away.
 */
export interface EditorState {
  readonly rows: readonly DraftRule[];
  readonly pendingOne: ReadonlySet<string>;
}

/** Rows the analyses can read: a pending "one resource" row is not yet a claim about anything. */
export function analysable(state: EditorState): readonly DraftRule[] {
  return state.rows.map((row) =>
    state.pendingOne.has(row.id) && row.resourceId === null
      ? { ...row, action: null }
      : row,
  );
}

/* ---------------------------------------------------------------- what one rule does */

/** Why a row does not mean what it reads as. Three kinds, and only the first is refused.
 *
 * * `contradiction` — the same role, action and target with the opposite effect. The platform
 *   refuses to store it, so it blocks saving rather than being a note.
 * * `shadowed` — a specific allow underneath a broad deny for the same role and action. Deny wins,
 *   so it grants **nothing** while the form shows an allow.
 * * `narrowed` — a broad allow with specific denies over it. True, except where it is not, and the
 *   exception is the half worth saying.
 */
export type RuleIssue =
  | { readonly kind: 'contradiction'; readonly by: readonly number[] }
  | { readonly kind: 'shadowed'; readonly by: readonly number[] }
  | { readonly kind: 'narrowed'; readonly by: readonly number[] };

/** One answer per row, by position. Incomplete rows have none: they are not yet a claim. */
export function issuesFor(rows: readonly DraftRule[]): readonly (RuleIssue | null)[] {
  const complete = rows
    .map((row, index) => ({ row, position: index + 1 }))
    .filter((each) => isComplete(each.row));

  return rows.map((row, index) => {
    if (!isComplete(row)) return null;
    const position = index + 1;
    const sameTarget = complete.filter(
      (other) =>
        other.position !== position &&
        other.row.role === row.role &&
        other.row.action === row.action &&
        other.row.resourceType === row.resourceType,
    );

    /* First, because it is the only one of the three the platform refuses outright. */
    const clash = sameTarget.filter(
      (other) =>
        other.row.effect !== row.effect && other.row.resourceId === row.resourceId,
    );
    if (clash.length > 0) {
      return { kind: 'contradiction', by: clash.map((each) => each.position) };
    }
    if (row.effect === 'allow' && row.resourceId === null) {
      const denies = sameTarget.filter(
        (other) => other.row.effect === 'deny' && other.row.resourceId !== null,
      );
      if (denies.length > 0)
        return { kind: 'narrowed', by: denies.map((each) => each.position) };
    }
    if (row.effect === 'allow' && row.resourceId !== null) {
      const broad = sameTarget.filter(
        (other) => other.row.effect === 'deny' && other.row.resourceId === null,
      );
      if (broad.length > 0)
        return { kind: 'shadowed', by: broad.map((each) => each.position) };
    }
    return null;
  });
}

/* ---------------------------------------------------------------- what the rules grant together */

/** How one action reads for one role on one resource type.
 *
 * * `every` — allowed on every resource of the type.
 * * `except` — allowed on every one except the named ids, which a specific deny takes back.
 * * `only` — allowed on the named ids and nothing else.
 * * `denied` — a broad deny, which wins over any allow.
 */
export type ActionMark = 'every' | 'except' | 'only' | 'denied';

export interface ActionReading {
  readonly action: string;
  readonly mark: ActionMark;
  /** The ids an `except` or `only` names; empty otherwise. */
  readonly ids: readonly string[];
}

export interface TypeReading {
  readonly resourceType: string;
  readonly actions: readonly ActionReading[];
}

export interface RoleReading {
  readonly role: string;
  readonly types: readonly TypeReading[];
}

/** What the complete rows grant, role by role. **A deny-only exception is not shown as a grant.**
 *
 * A specific deny with no allow beside it takes away something nobody was given, so it says
 * nothing about access and is left out rather than rendered as a refusal of one dataset. Roles,
 * and types within a role, appear in the order the rows first name them; actions follow the
 * vocabulary's order, so the summary reads the same way whichever rule was written first.
 */
export function accessSummary(
  rows: readonly DraftRule[],
  vocabulary: PolicyVocabulary | undefined,
): readonly RoleReading[] {
  const complete = rows.filter(isComplete);
  const roles = unique(complete.map((row) => row.role ?? ''));

  return roles
    .map((role) => {
      const mine = complete.filter((row) => row.role === role);
      const types = unique(mine.map((row) => row.resourceType ?? ''));
      return {
        role,
        types: types
          .map((resourceType) => ({
            resourceType,
            actions: actionOrder(mine, resourceType, vocabulary)
              .map((action) => readAction(mine, resourceType, action))
              .filter((reading): reading is ActionReading => reading !== null),
          }))
          .filter((type) => type.actions.length > 0),
      };
    })
    .filter((reading) => reading.types.length > 0);
}

function actionOrder(
  rows: readonly DraftRule[],
  resourceType: string,
  vocabulary: PolicyVocabulary | undefined,
): readonly string[] {
  const named = unique(
    rows
      .filter((row) => row.resourceType === resourceType)
      .map((row) => row.action ?? ''),
  );
  const declared =
    vocabulary?.resources.find((each) => each.resource_type === resourceType)?.actions ??
    [];
  return [
    ...declared.filter((action) => named.includes(action)),
    ...named.filter((action) => !declared.includes(action)),
  ];
}

function readAction(
  rows: readonly DraftRule[],
  resourceType: string,
  action: string,
): ActionReading | null {
  const matching = rows.filter(
    (row) => row.resourceType === resourceType && row.action === action,
  );
  if (matching.some((row) => row.effect === 'deny' && row.resourceId === null)) {
    return { action, mark: 'denied', ids: [] };
  }
  const deniedIds = matching
    .filter((row) => row.effect === 'deny' && row.resourceId !== null)
    .map((row) => row.resourceId ?? '');
  if (matching.some((row) => row.effect === 'allow' && row.resourceId === null)) {
    return deniedIds.length > 0
      ? { action, mark: 'except', ids: unique(deniedIds) }
      : { action, mark: 'every', ids: [] };
  }
  const allowedIds = unique(
    matching
      .filter((row) => row.effect === 'allow' && row.resourceId !== null)
      .map((row) => row.resourceId ?? '')
      .filter((id) => !deniedIds.includes(id)),
  );
  return allowedIds.length > 0 ? { action, mark: 'only', ids: allowedIds } : null;
}

function unique<T>(values: readonly T[]): readonly T[] {
  return [...new Set(values)];
}

/* ---------------------------------------------------------------- what a publish changes */

export interface RuleDiff {
  readonly added: readonly PolicyRule[];
  readonly removed: readonly PolicyRule[];
}

/** Rules in the draft and not in the active version, and the reverse. A changed effect is both. */
export function ruleDiff(
  before: readonly PolicyRule[],
  after: readonly PolicyRule[],
): RuleDiff {
  const had = new Set(before.map(keyOfRule));
  const has = new Set(after.map(keyOfRule));
  return {
    added: after.filter((rule) => !had.has(keyOfRule(rule))),
    removed: before.filter((rule) => !has.has(keyOfRule(rule))),
  };
}

/** The bump a diff suggests. **A suggestion, never applied silently** — the publish route takes
 * the caller's declaration, because whether a change is breaking is an opinion about semantics.
 * Removing a rule changes who is allowed, so it suggests major; only additions suggest minor. */
export function suggestedBump(diff: RuleDiff): BumpKind {
  if (diff.removed.length > 0) return 'major';
  if (diff.added.length > 0) return 'minor';
  return 'patch';
}

function parsed(semver: string): readonly [number, number, number] | null {
  const parts = semver.split('.').map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part) || part < 0)) {
    return null;
  }
  return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
}

/** The greatest semver among the versions, which is what the platform bumps from.
 *
 * **Not the active version's.** After a rollback the active version is not the newest, and the
 * server mints from `latest_semver` — so predicting from the active one would show a number that
 * already exists.
 */
export function latestSemverOf(versions: readonly BundleVersion[]): string | null {
  let best: readonly [number, number, number] | null = null;
  let label: string | null = null;
  for (const version of versions) {
    const semver = version.semver ?? null;
    if (semver === null) continue;
    const value = parsed(semver);
    if (value === null) continue;
    if (best === null || compare(value, best) > 0) {
      best = value;
      label = semver;
    }
  }
  return label;
}

function compare(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}

/** The version a publish will mint. The first one is always 1.0.0, whatever bump is declared. */
export function nextSemver(latest: string | null, bump: BumpKind): string {
  const value = latest === null ? null : parsed(latest);
  if (value === null) return '1.0.0';
  const [major, minor, patch] = value;
  if (bump === 'major') return `${String(major + 1)}.0.0`;
  if (bump === 'minor') return `${String(major)}.${String(minor + 1)}.0`;
  return `${String(major)}.${String(minor)}.${String(patch + 1)}`;
}

/** Versions in the order the history reads: the draft, the active one, then the rest newest first. */
export function orderVersions(
  versions: readonly BundleVersion[],
): readonly BundleVersion[] {
  const rank = (version: BundleVersion): number =>
    version.state === 'draft' ? 0 : version.state === 'active' ? 1 : 2;
  return [...versions].sort((a, b) => {
    const byState = rank(a) - rank(b);
    if (byState !== 0) return byState;
    const left = parsed(a.semver ?? '');
    const right = parsed(b.semver ?? '');
    if (left === null || right === null) return 0;
    return compare(right, left);
  });
}

/* ---------------------------------------------------------------- how one request is decided */

export interface AccessRequest {
  readonly role: string;
  readonly action: string;
  readonly resourceType: string;
  /** `null` for "any one" — a rule naming a specific resource then does not match. */
  readonly resourceId: string | null;
}

/** A rule as the checker sees it: what it says and where it sits in the list being checked. */
export interface NumberedRule {
  readonly rule: PolicyRule;
  readonly position: number;
}

/** What the attribute-policy step evaluates against, decided before any rule is read.
 *
 * Taken from the tenant's real enforcement reading, so the checker answers for the platform as it
 * is: with policy off, or no engine, or no governing bundle, roles decide alone; with a governing
 * bundle and nothing active, every request is refused.
 */
export type PolicyStage =
  | {
      readonly kind: 'skipped';
      readonly reason: 'no-engine' | 'off' | 'nothing-to-apply';
    }
  | { readonly kind: 'refuses-all' }
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'rules'; readonly rules: readonly NumberedRule[] };

export type StepOutcome = 'pass' | 'fail' | 'skip' | 'unknown';

export type PolicyStep =
  | {
      readonly kind: 'skipped';
      readonly reason: 'no-engine' | 'off' | 'nothing-to-apply';
    }
  | { readonly kind: 'refuses-all' }
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'denied-by'; readonly match: NumberedRule }
  | { readonly kind: 'allowed-by'; readonly match: NumberedRule }
  | { readonly kind: 'no-match' };

export interface AccessCheck {
  readonly verdict: 'allowed' | 'refused' | 'unknown';
  /** `null` when the role's grants could not be read. */
  readonly roleGrants: boolean | null;
  readonly policy: PolicyStep;
  /** Some rule for this role and action names a specific resource, and none was chosen. */
  readonly namesResources: boolean;
}

/** Whether a request would be allowed, and which step decides.
 *
 * The role step comes first because roles are always consulted and policy can only narrow: a role
 * that does not hold the permission is refused whatever the rules say. The policy step is still
 * reported when the role's grants could not be read, because a refusal there is certain either way.
 */
export function checkAccess(
  request: AccessRequest,
  roleGrants: boolean | null,
  stage: PolicyStage,
): AccessCheck {
  const policy = policyStep(request, stage);
  const policyOutcome = outcomeOf(policy);
  const namesResources =
    request.resourceId === null &&
    stage.kind === 'rules' &&
    stage.rules.some(
      ({ rule }) =>
        rule.resource_id !== null &&
        rule.role === request.role &&
        rule.action === request.action &&
        rule.resource_type === request.resourceType,
    );

  let verdict: AccessCheck['verdict'];
  if (roleGrants === false || policyOutcome === 'fail') verdict = 'refused';
  else if (roleGrants === null || policyOutcome === 'unknown') verdict = 'unknown';
  else verdict = 'allowed';

  return { verdict, roleGrants, policy, namesResources };
}

function policyStep(request: AccessRequest, stage: PolicyStage): PolicyStep {
  if (stage.kind !== 'rules') return stage;
  const matching = stage.rules.filter(
    ({ rule }) =>
      rule.role === request.role &&
      rule.action === request.action &&
      rule.resource_type === request.resourceType &&
      (rule.resource_id === null || rule.resource_id === request.resourceId),
  );
  const deny = matching.find(({ rule }) => rule.effect === 'deny');
  if (deny !== undefined) return { kind: 'denied-by', match: deny };
  const allow = matching.find(({ rule }) => rule.effect === 'allow');
  if (allow !== undefined) return { kind: 'allowed-by', match: allow };
  return { kind: 'no-match' };
}

/** How a policy step reads as a pass or a fail. Total over the step kinds. */
export function outcomeOf(step: PolicyStep): StepOutcome {
  switch (step.kind) {
    case 'skipped':
      return 'skip';
    case 'allowed-by':
      return 'pass';
    case 'unreadable':
      return 'unknown';
    case 'refuses-all':
    case 'denied-by':
    case 'no-match':
      return 'fail';
  }
}

/** The stage the *active* policy puts a request through, from the tenant's enforcement reading.
 *
 * `rules` is `undefined` while the active version's rules are being read and `null` when the version
 * was uploaded as compiled policy, which has no rules to check against.
 */
export function activeStage(
  reading: EnforcementReading,
  rules: readonly PolicyRule[] | null | undefined,
): PolicyStage {
  switch (reading) {
    case 'no-engine':
      return { kind: 'skipped', reason: 'no-engine' };
    case 'abac-off':
      return { kind: 'skipped', reason: 'off' };
    case 'on-without-policy':
      return { kind: 'skipped', reason: 'nothing-to-apply' };
    case 'failing-closed':
      return { kind: 'refuses-all' };
    case 'enforcing':
      if (rules === undefined || rules === null) return { kind: 'unreadable' };
      return { kind: 'rules', rules: numbered(rules) };
  }
}

/** Rules numbered by their position in the list they came from. */
export function numbered(rules: readonly PolicyRule[]): readonly NumberedRule[] {
  return rules.map((rule, index) => ({ rule, position: index + 1 }));
}

/** The grants a role holds, from the stored `action:resource_type` permissions. */
export function roleGrants(
  roles:
    | readonly { readonly name: string; readonly permissions: readonly string[] }[]
    | undefined,
  request: AccessRequest,
): boolean | null {
  if (roles === undefined) return null;
  const role = roles.find((each) => each.name === request.role);
  if (role === undefined) return false;
  return role.permissions.includes(`${request.action}:${request.resourceType}`);
}
